import React, { useEffect, useRef, useState } from 'react';
import {
  calculateTimelapse,
  formatDuration,
  formatDurationInput,
  formatInterval,
  formatRecordingDuration,
  formatCalendarDuration,
  countDaysPerWeek,
  calcParamsFromState,
  deriveDisplayStats,
  scenarioSwitchPatch,
  solveForToMode,
  modeToSolveFor,
  FPS_OPTIONS,
  INTERVAL_CHIP_SECONDS,
  PACE_CEILING,
} from '../lib/timelapseCalc';
import './TimelapseCalculatorPage.css';

// Shared, presentational calculator core: scenario tabs + solve-for + input
// form + answer panel. It owns no persistence and no analytics — the parent
// controls `value` and reacts to `onChange`/`onSolveForChange`/
// `onScenarioChange`, so the same core powers both the public marketing page
// and the in-project "capture plan" dialog. See TimelapseCalculatorPage (page
// wrapper) and project/CapturePlanCard (dialog wrapper). Pure math + shared
// constants live in ../lib/timelapseCalc.

export const STORAGE_PRESETS = [
  { id: 'jpeg_12mp', label: '12 MP JPEG (≈5 MB)', mb: 5 },
  { id: 'jpeg_20mp', label: '20 MP JPEG (≈8 MB)', mb: 8 },
  { id: 'raw_24mp', label: '24 MP RAW (≈30 MB)', mb: 30 },
  { id: 'video_4k', label: '4K video frame (≈2 MB)', mb: 2 },
  { id: 'custom', label: 'Custom (MB per frame)', mb: null },
];

// The desktop breakpoint, where TimelapseCalculatorPage.css sets
// `.calc-pill { display: none }`. Exported so a unit test can assert the two
// stay in lockstep — if the stylesheet's breakpoint moves and this does not,
// the pill silently comes back at the prerender viewport.
export const PILL_HIDDEN_QUERY = '(min-width: 900px)';

// How far the page must have scrolled before the sticky answer pill is allowed
// back on screen. Roughly one pill height: at 390x844 the pill sits directly
// over the Capture-interval field at scroll 0, so a smaller threshold puts it
// straight back over the control it was covering — a tap that drags a pixel,
// or iOS rubber-banding leaving a non-zero scrollY, is enough.
export const PILL_MIN_SCROLL = 64;

const WEEKDAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const WEEKDAY_ARIA = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// ---------- Scenario icons ----------
// Drawn rather than imported: three 16px line glyphs, same stroke weight, so
// the tab row reads as one control instead of three pasted images.
const IconShoot = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M3 7.5h3.2l1.5-2h8.6l1.5 2H21v11H3z" strokeLinejoin="round" />
    <circle cx="12" cy="13" r="3.4" />
  </svg>
);

const IconProject = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15" rx="2" />
    <path d="M3.5 10h17M8 3.5v3M16 3.5v3" strokeLinecap="round" />
  </svg>
);

const IconDrive = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M9.5 3.5 7 20.5M14.5 3.5 17 20.5" strokeLinecap="round" />
    <path d="M12 6v3M12 13.5v3.5" strokeLinecap="round" />
  </svg>
);

const SCENARIO_TABS = [
  { id: 'shoot', label: 'Single shoot', Icon: IconShoot },
  { id: 'project', label: 'Long-term project', Icon: IconProject },
  { id: 'drive', label: 'Driving', Icon: IconDrive },
];

const SOLVE_OPTIONS = [
  { id: 'length', label: 'Video length' },
  { id: 'interval', label: 'Interval' },
  { id: 'duration', label: 'Recording time' },
];

// What the "still footage" share actually is, in each scenario's own terms.
const STILL_HINTS = {
  shoot: 'Clouds that stall, empty frames.',
  project: 'Lunch breaks, idle site days.',
  drive: 'Red lights, traffic.',
};

// Quick picks for the still-footage share. "None" is first and is the obvious
// way to skip the whole comparison; the slider underneath still gives anyone
// who wants a specific number one.
const STILL_CHIPS = [
  { id: 'none', label: 'None', value: 0 },
  { id: 'little', label: 'A little', value: 0.15 },
  { id: 'some', label: 'Some', value: 0.25 },
  { id: 'lot', label: 'A lot', value: 0.4 },
];

const SHOOT_UNITS = [
  { id: 'minutes', label: 'min' },
  { id: 'hours', label: 'hr' },
  { id: 'days', label: 'days' },
];

const speedUnitFor = (distanceUnit) => (distanceUnit === 'km' ? 'km/h' : 'mph');

function minutesToTimeString(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function timeStringToMinutes(str) {
  if (!str || typeof str !== 'string') return null;
  const [h, m] = str.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

const round0 = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString() : '—');

// ---------- Numeric input with a local draft buffer ----------
// A controlled <input type="number"> that lets the user fully clear or partially
// type a value without the results panel flipping to an error. The parent still
// owns the committed number (`value`) and the calculations still update live —
// but while the field is empty or mid-edit we hold that raw text locally and DO
// NOT push NaN upstream, so the last valid number keeps driving the results.
//
// Contract: `onCommit(number)` fires on every keystroke that parses to a real
// number; an empty/partial entry ("", "-", "1.") is held silently. On blur the
// field normalizes back to the committed value, so a field left empty snaps to
// the last good number instead of staying blank.
function NumberField({ value, onCommit, format, ...inputProps }) {
  // null → mirror the committed `value`; a string → the user's in-progress text.
  const [draft, setDraft] = useState(null);
  const [prevValue, setPrevValue] = useState(value);
  const lastCommittedRef = useRef(value);

  // Drop the draft when `value` changes from OUTSIDE this field (preset, URL
  // load, unit switch) — but keep it when the change is the echo of our own
  // onCommit, so the text the user is typing is never yanked out from under them.
  if (value !== prevValue) {
    setPrevValue(value);
    if (value !== lastCommittedRef.current) setDraft(null);
    lastCommittedRef.current = value;
  }

  const fmt = format || ((v) => (Number.isFinite(v) ? String(v) : ''));
  const shown = draft != null ? draft : fmt(value);

  const handleChange = (e) => {
    const text = e.target.value;
    setDraft(text);
    if (text.trim() === '') return; // fully cleared — hold, never commit NaN
    const n = parseFloat(text);
    if (Number.isNaN(n)) return; // partial like "-" or "." — hold the text
    lastCommittedRef.current = n;
    onCommit(n);
  };

  const handleBlur = () => setDraft(null); // normalize to the committed value

  return (
    <input
      type="number"
      value={shown}
      onChange={handleChange}
      onBlur={handleBlur}
      {...inputProps}
    />
  );
}

// ---------- Interval / target-length compound input ----------
// Wraps a number + unit (s / min / hr) into a single "seconds" value.
function IntervalInput({ id, seconds, onChange, compactUnits, testIdPrefix }) {
  const getBestUnit = (secs) => (
    secs >= 3600 && secs % 3600 === 0
      ? 'hr'
      : secs >= 60 && secs % 60 === 0
        ? 'min'
        : 's'
  );
  const [unit, setUnit] = useState(() => getBestUnit(seconds));
  // Re-derive the unit only when `seconds` changes from OUTSIDE (preset,
  // URL load) — changes we sent via onChange keep the unit the user typed.
  const [prevSeconds, setPrevSeconds] = useState(seconds);
  const lastSentSecondsRef = useRef(seconds);
  if (seconds !== prevSeconds) {
    setPrevSeconds(seconds);
    if (seconds !== lastSentSecondsRef.current) {
      setUnit(getBestUnit(seconds));
    }
    lastSentSecondsRef.current = seconds;
  }
  const factor = unit === 'hr' ? 3600 : unit === 'min' ? 60 : 1;
  const displayValue = seconds / factor;

  const commitNumber = (v) => {
    const nextSeconds = v * factor;
    lastSentSecondsRef.current = nextSeconds;
    onChange(nextSeconds);
  };
  const handleUnit = (e) => setUnit(e.target.value);

  const prefix = testIdPrefix || 'interval';

  return (
    <div className="calc-inline-row">
      <NumberField
        id={id}
        min="0.1"
        step="0.1"
        value={displayValue}
        onCommit={commitNumber}
        data-testid={`${prefix}-value`}
      />
      <select
        aria-label="Interval unit"
        value={unit}
        onChange={handleUnit}
        data-testid={`${prefix}-unit`}
      >
        <option value="s">{compactUnits ? 's' : 'seconds'}</option>
        <option value="min">{compactUnits ? 'min' : 'minutes'}</option>
        <option value="hr">{compactUnits ? 'hr' : 'hours'}</option>
      </select>
    </div>
  );
}

// ---------- Segmented radio group ----------
// One control shape for every either/or choice on the page (solve-for, units,
// frame rate) so the form reads as a set of switches rather than a pile of
// unrelated widgets. Arrow keys move the selection, as a radiogroup should.
function Segmented({ options, value, onChange, ariaLabel, testIdPrefix, className = '' }) {
  const refs = useRef({});
  const handleKeyDown = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const idx = options.findIndex((o) => o.id === value);
    const step = e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1;
    const next = options[(idx + step + options.length) % options.length];
    onChange(next.id);
    const node = refs.current[next.id];
    if (node) node.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`calc-segmented ${className}`.trim()}
      onKeyDown={handleKeyDown}
    >
      {options.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            ref={(n) => { refs.current[o.id] = n; }}
            className={`calc-seg${active ? ' is-active' : ''}`}
            data-testid={`${testIdPrefix}-${o.id}`}
            onClick={() => onChange(o.id)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------- Presentational core ----------
// Props:
//   value            : the input state snapshot (see DEFAULT_CALCULATOR_STATE)
//   onChange         : (updates, control) => void — parent merges into value
//   onSolveForChange : (solveFor) => void — optional; analytics hook
//   onScenarioChange : (scenario) => void — optional; analytics + drive seeding
//   compact          : tighter layout for the in-dialog use
//   presetRow        : node rendered above the inputs (page example chips)
//   panelFooter      : node rendered inside the answer panel (page share row)
//   asideFooter      : node rendered below the answer panel (CTA card)
export default function TimelapseCalculator({
  value,
  onChange,
  onSolveForChange,
  onScenarioChange,
  compact = false,
  presetRow = null,
  panelFooter = null,
  asideFooter = null,
}) {
  const state = value;
  const scenario = state.scenario || 'project';
  const solveFor = state.solveFor || modeToSolveFor(state.mode);
  const tabRefs = useRef({});
  const answerRef = useRef(null);

  // Local UI-only flag: whether the custom MB input is revealed. Reset it
  // when mbPerFrame changes from OUTSIDE (e.g. a preset), mirroring the
  // IntervalInput unit-resync pattern.
  const [customStorage, setCustomStorage] = useState(false);
  const [prevMb, setPrevMb] = useState(state.mbPerFrame);
  const lastSelfMbRef = useRef(state.mbPerFrame);
  if (state.mbPerFrame !== prevMb) {
    setPrevMb(state.mbPerFrame);
    if (state.mbPerFrame !== lastSelfMbRef.current) setCustomStorage(false);
    lastSelfMbRef.current = state.mbPerFrame;
  }

  // The sticky answer pill re-surfaces the answer once it has scrolled away,
  // so it is driven by an IntersectionObserver on the answer card. Where there
  // is no observer (jsdom, very old browsers) there is no way to know when to
  // hide it, and a pill pinned over the page forever is worse than none, so it
  // is not rendered at all.
  //
  // This route is prerendered: scripts/prerender.js snapshots the DOM after
  // mount effects settle and then re-hydrates that snapshot, so the first
  // render has to match the settled markup or React bails out of hydration.
  // `null` means the observer has not reported yet, which keeps the pill out
  // of the first render; it appears only once the observer explicitly reports
  // the answer card off screen.
  //
  // The observer is also skipped outright at the desktop breakpoint, where the
  // stylesheet hides the pill anyway (PILL_HIDDEN_QUERY). That is the other
  // half of the hydration guard: prerender.js snapshots at a fixed 1280x900
  // viewport, so never observing there keeps the pill out of the *snapshot* by
  // construction. Without it the snapshot would still depend on layout — add
  // content above the calculator, push the answer card below 900px, and the
  // pill lands in the snapshot while the first client render has none, which
  // is the same React #418 this change exists to kill.
  //
  // A second gate: the pill also stays out until the page has scrolled at
  // least PILL_MIN_SCROLL. At 390x844 the fixed pill lands exactly over the
  // Capture-interval input, so at first paint a tap meant for that field hit
  // the pill instead, and one pill height is what it takes for that control to
  // clear the bottom edge. Once the user has scrolled that far they have
  // chosen what sits there, and scrolling back up hides it again.
  // `nearTop` starts `true` and is only ever written from the scroll listener
  // inside the effect, so the first render stays deterministic and the
  // prerendered snapshot (taken at scroll 0) still has no pill. It is NOT
  // reset on teardown: the only way back to the phone layout re-attaches the
  // listener and re-reads window.scrollY, so a user who is genuinely scrolled
  // down when they resize desktop -> phone correctly gets the pill back.
  const hasIO = typeof IntersectionObserver === 'function';
  const [answerOnScreen, setAnswerOnScreen] = useState(null);
  const [nearTop, setNearTop] = useState(true);
  useEffect(() => {
    if (compact || !hasIO) return undefined;
    const el = answerRef.current;
    if (!el) return undefined;

    // Absent in jsdom; "can't tell" means assume the phone layout, which is
    // the one that actually needs the pill.
    const mq =
      typeof window.matchMedia === 'function' ? window.matchMedia(PILL_HIDDEN_QUERY) : null;

    let io = null;
    let scrolling = false;
    const onScroll = () => setNearTop(window.scrollY <= PILL_MIN_SCROLL);
    const stop = () => {
      if (scrolling) {
        window.removeEventListener('scroll', onScroll);
        scrolling = false;
      }
      if (!io) return;
      io.disconnect();
      io = null;
    };
    // Also runs on a resize across the breakpoint, so the pill attaches on a
    // desktop-to-phone resize and drops on the way back, rather than waiting
    // for the next mount.
    const sync = () => {
      if (mq && mq.matches) {
        stop();
        setAnswerOnScreen(null);
        return;
      }
      if (io) return;
      io = new IntersectionObserver(
        (entries) => setAnswerOnScreen(entries[entries.length - 1].isIntersecting),
        { threshold: 0 }
      );
      io.observe(el);
      window.addEventListener('scroll', onScroll, { passive: true });
      scrolling = true;
      // A page restored from bfcache or opened on an anchor is already
      // scrolled, and no scroll event is coming to say so.
      onScroll();
    };

    sync();
    if (!mq) return stop;
    // Safari < 14 only has the deprecated addListener/removeListener pair.
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
      stop();
    };
  }, [compact, hasIO]);

  const patch = (updates, control) => onChange(updates, control);

  const setSolveFor = (next) => {
    onChange({ solveFor: next, mode: solveForToMode(next) }, 'solveFor');
    if (onSolveForChange) onSolveForChange(next);
  };

  const setScenario = (next) => {
    if (next === scenario) return;
    onChange(scenarioSwitchPatch(state, next), 'scenario');
    if (onScenarioChange) onScenarioChange(next);
  };

  const daysPerWeek = countDaysPerWeek(state.weekdayMask);
  const result = calculateTimelapse(calcParamsFromState(state));
  const errorMap = {};
  if (!result.ok) result.errors.forEach((e) => { errorMap[e.field] = e.message; });
  const stats = deriveDisplayStats(result, state);

  const distanceUnit = state.distanceUnit === 'km' ? 'km' : 'mi';
  const speedUnit = speedUnitFor(distanceUnit);

  // ---------- Answer copy ----------
  // One sentence that reads back the whole plan, so the number above it is
  // never a bare figure the reader has to reverse-engineer.
  const recordedPhrase = () => {
    if (scenario === 'drive') {
      return `Drive ${state.distanceValue} ${distanceUnit} at ${state.speedValue} ${speedUnit}`;
    }
    const duration = formatDurationInput(state.durationValue, state.durationUnit);
    if (scenario === 'project') {
      return `Shoot for ${duration} at ${daysPerWeek} day${daysPerWeek === 1 ? '' : 's'} a week`;
    }
    return `Shoot for ${duration}`;
  };

  // The still-footage allowance is an input now, and it sits at the far end of
  // the form, so the two solves it actually moves say so in the sentence.
  const stillPercent = Math.round(state.deadTimeFraction * 100);
  const allowanceClause = stillPercent > 0
    ? `, allowing for ${stillPercent}% still footage`
    : '';

  const answer = (() => {
    if (!result.ok) {
      return { kicker: 'Check the inputs', value: '—', sentence: 'Fix the highlighted field and the numbers come back.' };
    }
    if (solveFor === 'interval') {
      // The clip length quoted here is the SNAPPED one, not the target the
      // user typed: the recommended interval is rounded to a stop a camera can
      // actually be dialled to, so the plan on screen lands near the target
      // rather than on it. Quoting the input instead would contradict the
      // MotionTrim bar directly below, which reads the same snapped number.
      return {
        kicker: 'Shoot one photo',
        value: formatInterval(result.recommendedIntervalSeconds).replace(/^every /, 'every '),
        sentence: `${recordedPhrase()} at that interval and you land about a ${formatDuration(stats.trimmedVideoSeconds)} clip at ${state.fps} fps${allowanceClause}.`,
      };
    }
    if (solveFor === 'duration') {
      const calendar = scenario === 'project' && result.requiredCalendarDays > 1
        ? ` That is about ${formatCalendarDuration(result.requiredCalendarDays * 24 * 3600)} on site at ${daysPerWeek} day${daysPerWeek === 1 ? '' : 's'} a week.`
        : '';
      return {
        kicker: 'Keep recording for',
        value: formatRecordingDuration(result.requiredRecordingSeconds),
        sentence: `Record that long at one photo ${formatInterval(state.intervalSeconds)} to fill a ${formatDuration(state.targetVideoSeconds)} clip at ${state.fps} fps${allowanceClause}.${calendar}`,
      };
    }
    return {
      kicker: 'Your clip runs',
      value: formatDuration(result.rawVideoSeconds),
      sentence: `${recordedPhrase()}, one photo ${formatInterval(state.intervalSeconds)}, and you have ${round0(result.frameCount)} frames. At ${state.fps} fps that plays for ${formatDuration(result.rawVideoSeconds)}, ${round0(result.speedup)}× real time.`,
    };
  })();

  // ---------- Finish comparison ----------
  const finishRows = stats ? [
    { id: 'constant', label: 'Constant speed', note: 'Every frame, one speed', seconds: stats.rawVideoSeconds },
    { id: 'trim', label: 'MotionTrim', note: 'Cuts the still parts', seconds: stats.trimmedVideoSeconds },
    { id: 'pace', label: 'MotionPace', note: `Speeds through them, up to ${PACE_CEILING}×`, seconds: stats.pacedVideoSeconds },
  ] : [];
  const finishMax = finishRows.length
    ? Math.max(...finishRows.map((r) => r.seconds))
    : 1;
  // At zero still footage all three finishes are the same length, so three
  // identical bars would be three ways of saying nothing. One line instead.
  const finishMode = !stats ? 'empty' : (stillPercent === 0 ? 'flat' : 'bars');

  const activeStorageId = customStorage
    ? 'custom'
    : (STORAGE_PRESETS.find((s) => s.mb === state.mbPerFrame) || { id: 'custom' }).id;

  const toggleWeekday = (idx) => {
    const next = [...state.weekdayMask];
    next[idx] = !next[idx];
    if (next.some(Boolean)) patch({ weekdayMask: next }, 'daysPerWeek');
  };

  const handleStorageSelect = (opt) => {
    if (opt.id === 'custom') {
      setCustomStorage(true);
      return;
    }
    setCustomStorage(false);
    lastSelfMbRef.current = opt.mb;
    patch({ mbPerFrame: opt.mb }, 'mbPerFrame');
  };

  const handleTabKeyDown = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const idx = SCENARIO_TABS.findIndex((t) => t.id === scenario);
    const step = e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1;
    const next = SCENARIO_TABS[(idx + step + SCENARIO_TABS.length) % SCENARIO_TABS.length];
    setScenario(next.id);
    const node = tabRefs.current[next.id];
    if (node) node.focus();
  };

  const solveOptions = scenario === 'drive'
    ? SOLVE_OPTIONS.filter((o) => o.id !== 'duration')
    : SOLVE_OPTIONS;

  const showPill = !compact && hasIO && answerOnScreen === false && !nearTop && result.ok;

  return (
    <div className={`calc-embedded${compact ? ' is-compact' : ''}`} data-scenario={scenario}>
      {/* ---------- Scenario tabs ---------- */}
      <div
        role="tablist"
        aria-label="What are you shooting"
        className="calc-tablist"
        data-scenario={scenario}
        onKeyDown={handleTabKeyDown}
      >
        {SCENARIO_TABS.map((t) => {
          const active = t.id === scenario;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`calc-tab-${t.id}`}
              ref={(n) => { tabRefs.current[t.id] = n; }}
              aria-selected={active}
              aria-controls="calc-panel"
              tabIndex={active ? 0 : -1}
              className={`calc-tab${active ? ' is-active' : ''}`}
              data-testid={`scenario-${t.id}`}
              onClick={() => setScenario(t.id)}
            >
              <t.Icon />
              <span className="calc-tab-title">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* ---------- Solve-for ---------- */}
      <div className="calc-solvebar">
        <span className="calc-solvebar-label" id="calc-solve-label">I want to know</span>
        <Segmented
          options={solveOptions}
          value={solveFor}
          onChange={setSolveFor}
          ariaLabel="What to calculate"
          testIdPrefix="solve"
          className="calc-segmented--solve"
        />
      </div>

      <section
        className="calc-columns"
        id="calc-panel"
        role="tabpanel"
        aria-labelledby={`calc-tab-${scenario}`}
      >
        {/* ---------- Answer ---------- */}
        <aside className="calc-results" aria-label="Calculation results">
          <div
            className={`calc-answer${!result.ok ? ' is-invalid' : ''}`}
            ref={answerRef}
            data-testid="calc-answer"
            aria-live="polite"
            aria-atomic="true"
          >
            <div className="calc-hero-stat" data-testid="hero-stat">
              <div className="calc-hero-stat-label">{answer.kicker}</div>
              <div className="calc-hero-stat-value" data-testid="hero-stat-value">
                {answer.value}
              </div>
              <p className="calc-answer-sentence" data-testid="answer-sentence">{answer.sentence}</p>
              {solveFor === 'interval' && result.ok && (
                <p className="calc-footnote" data-testid="exact-interval">
                  Exact interval: every {result.exactIntervalSeconds.toFixed(1)} s, snapped to the
                  nearest common stop.
                </p>
              )}
              {scenario === 'drive' && result.ok && stats && (
                <p className="calc-drive-note" data-testid="drive-note">
                  That is one frame every {stats.metresPerFrame.toFixed(1)} m
                  ({Math.round(stats.feetPerFrame)} ft).
                  <span className={`calc-feel calc-feel--${stats.driveFeel}`} data-testid="drive-feel">
                    {stats.driveFeel === 'smooth' ? 'Smooth' : stats.driveFeel === 'brisk' ? 'Brisk' : 'Jumpy'}
                  </span>
                  {stats.driveFeel === 'jumpy' && (
                    <span className="calc-feel-hint" data-testid="drive-feel-hint">
                      {' '}Try 0.5 s between frames to settle it down.
                    </span>
                  )}
                </p>
              )}
            </div>

            {/* ---------- Stats ---------- */}
            <div className="calc-stat-grid">
              <div className="calc-stat">
                <div className="calc-stat-label">Frames</div>
                <div className="calc-stat-value" data-testid="stat-frames">
                  {stats ? round0(stats.frameCount) : '—'}
                </div>
              </div>
              <div className="calc-stat">
                <div className="calc-stat-label">Real time captured</div>
                <div className="calc-stat-value" data-testid="stat-shooting">
                  {result.ok ? formatRecordingDuration(result.totalShootingSeconds) : '—'}
                </div>
              </div>
              <div className="calc-stat">
                <div className="calc-stat-label">Speed ratio</div>
                <div className="calc-stat-value" data-testid="stat-speedup">
                  {stats ? `${round0(stats.speedup)}×` : '—'}
                </div>
              </div>
              <div className="calc-stat">
                <div className="calc-stat-label">Storage</div>
                <div className="calc-stat-value" data-testid="stat-storage">
                  {stats ? `${stats.storageGB.toFixed(1)} GB` : '—'}
                </div>
              </div>
              {scenario === 'project' && (
                <div className="calc-stat">
                  <div className="calc-stat-label">Frames per day</div>
                  <div className="calc-stat-value" data-testid="stat-frames-per-day">
                    {stats ? round0(stats.framesPerDay) : '—'}
                  </div>
                </div>
              )}
              {scenario === 'drive' && (
                <div className="calc-stat">
                  <div className="calc-stat-label">Frames per {distanceUnit}</div>
                  <div className="calc-stat-value" data-testid="stat-frames-per-distance">
                    {stats ? round0(stats.framesPerDistanceUnit) : '—'}
                  </div>
                </div>
              )}
            </div>

            {/* ---------- Finish comparison ---------- */}
            {/* Sits under the stats: the plan first, then what each finish
                would do to its length. The still-footage share that drives it
                is the last field in the input card. */}
            <div className="calc-finish" data-testid="finish-block">
              <div className="calc-finish-head">
                <h3 className="calc-finish-title">What you&apos;ll get</h3>
                {stillPercent > 0 && (
                  <span className="calc-finish-still" data-testid="finish-still-note">
                    at {stillPercent}% still footage
                  </span>
                )}
              </div>

              {finishMode === 'empty' && <p className="calc-hint">No numbers yet.</p>}

              {finishMode === 'flat' && (
                <p className="calc-finish-flat" data-testid="finish-flat">
                  Every finish runs {formatDuration(stats.rawVideoSeconds)}. Set some still
                  footage to compare MotionTrim and MotionPace.
                </p>
              )}

              {finishMode === 'bars' && (
                <>
                  <div className="calc-bars">
                    {finishRows.map((row) => (
                      <div className="calc-bar-row" key={row.id} data-testid={`finish-row-${row.id}`}>
                        <div className="calc-bar-head">
                          <span className="calc-bar-label">{row.label}</span>
                          <span className="calc-bar-value" data-testid={`finish-value-${row.id}`}>
                            {formatDuration(row.seconds)}
                          </span>
                        </div>
                        <div className="calc-bar-track">
                          <span
                            className={`calc-bar-fill calc-bar-fill--${row.id}`}
                            data-testid={`finish-fill-${row.id}`}
                            style={{ width: `${Math.max(2, (row.seconds / finishMax) * 100)}%` }}
                          />
                        </div>
                        <span className="calc-bar-note">{row.note}</span>
                      </div>
                    ))}
                  </div>
                  <p className="calc-finish-foot">
                    MotionPace estimate assumes still stretches play at the {PACE_CEILING}× default ceiling.
                  </p>
                </>
              )}
            </div>

            {panelFooter}
          </div>

          {asideFooter}
        </aside>

        {/* ---------- Inputs ---------- */}
        <div className="calc-inputs" aria-label="Timelapse inputs" data-testid="calc-inputs">
          {presetRow}

          {scenario === 'shoot' && solveFor !== 'duration' && (
            <div className="calc-field">
              <label htmlFor="calc-duration">Shoot length</label>
              <div className="calc-inline-row">
                <NumberField
                  id="calc-duration"
                  min="1"
                  step="1"
                  value={state.durationValue}
                  onCommit={(v) => patch({ durationValue: v }, 'durationValue')}
                  data-testid="input-duration-value"
                />
                <Segmented
                  options={SHOOT_UNITS}
                  value={state.durationUnit}
                  onChange={(u) => patch({ durationUnit: u }, 'durationUnit')}
                  ariaLabel="Shoot length unit"
                  testIdPrefix="shoot-unit"
                />
              </div>
              {errorMap.durationValue && (
                <p className="calc-error" role="alert">{errorMap.durationValue}</p>
              )}
            </div>
          )}

          {scenario === 'project' && (
            <>
              {/* The duration is the answer when solving for recording time,
                  so the field goes away. The weekday mask and daily window
                  stay: they are what turn the answer into calendar time. */}
              {solveFor !== 'duration' && (
              <div className="calc-field">
                <label htmlFor="calc-duration">Total project duration</label>
                <div className="calc-inline-row">
                  <NumberField
                    id="calc-duration"
                    min="1"
                    step="1"
                    value={state.durationValue}
                    onCommit={(v) => patch({ durationValue: v }, 'durationValue')}
                    data-testid="input-duration-value"
                  />
                  <select
                    aria-label="Duration unit"
                    value={state.durationUnit}
                    onChange={(e) => patch({ durationUnit: e.target.value }, 'durationUnit')}
                    data-testid="input-duration-unit"
                  >
                    <option value="days">days</option>
                    <option value="weeks">weeks</option>
                    <option value="months">months</option>
                  </select>
                </div>
                {errorMap.durationValue && (
                  <p className="calc-error" role="alert">{errorMap.durationValue}</p>
                )}
              </div>
              )}

              <div className="calc-field">
                <span className="calc-label">Shooting days per week</span>
                <div className="calc-weekday-row" role="group" aria-label="Shooting days per week">
                  {WEEKDAY_LABELS.map((lbl, i) => {
                    const active = state.weekdayMask[i];
                    return (
                      <button
                        key={i}
                        type="button"
                        className={`calc-weekday-pill${active ? ' is-active' : ''}`}
                        aria-pressed={active}
                        aria-label={WEEKDAY_ARIA[i]}
                        data-testid={`weekday-${i}`}
                        onClick={() => toggleWeekday(i)}
                      >
                        {lbl}
                      </button>
                    );
                  })}
                </div>
                <p className="calc-hint" data-testid="days-per-week-count">
                  {daysPerWeek} {daysPerWeek === 1 ? 'day' : 'days'} per week
                </p>
                {errorMap.daysPerWeek && (
                  <p className="calc-error" role="alert">{errorMap.daysPerWeek}</p>
                )}
              </div>

              <div className="calc-field">
                <span className="calc-label">Daily shooting window</span>
                <label className="calc-check-row">
                  <input
                    type="checkbox"
                    checked={state.is24h}
                    onChange={(e) => patch({ is24h: e.target.checked }, 'is24h')}
                    data-testid="input-24h"
                  />
                  <span>24 hours (round-the-clock capture)</span>
                </label>
                {!state.is24h && (
                  <div className="calc-inline-row">
                    <label className="calc-time-label">
                      Start
                      <input
                        type="time"
                        value={minutesToTimeString(state.dailyStartMin)}
                        onChange={(e) => {
                          const m = timeStringToMinutes(e.target.value);
                          if (m != null) patch({ dailyStartMin: m }, 'dailyStartMin');
                        }}
                        data-testid="input-daily-start"
                      />
                    </label>
                    <label className="calc-time-label">
                      End
                      <input
                        type="time"
                        value={minutesToTimeString(state.dailyEndMin)}
                        onChange={(e) => {
                          const m = timeStringToMinutes(e.target.value);
                          if (m != null) patch({ dailyEndMin: m }, 'dailyEndMin');
                        }}
                        data-testid="input-daily-end"
                      />
                    </label>
                  </div>
                )}
                {errorMap.dailyWindow && (
                  <p className="calc-error" role="alert">{errorMap.dailyWindow}</p>
                )}
              </div>
            </>
          )}

          {scenario === 'drive' && (
            <>
              <div className="calc-field">
                <label htmlFor="calc-distance">Distance</label>
                <div className="calc-inline-row">
                  <NumberField
                    id="calc-distance"
                    min="0.1"
                    step="0.1"
                    value={state.distanceValue}
                    onCommit={(v) => patch({ distanceValue: v }, 'distanceValue')}
                    data-testid="input-distance-value"
                  />
                  <Segmented
                    options={[{ id: 'mi', label: 'mi' }, { id: 'km', label: 'km' }]}
                    value={distanceUnit}
                    onChange={(u) => patch({ distanceUnit: u }, 'distanceUnit')}
                    ariaLabel="Distance unit"
                    testIdPrefix="distance-unit"
                  />
                </div>
                {errorMap.distanceValue && (
                  <p className="calc-error" role="alert">{errorMap.distanceValue}</p>
                )}
              </div>

              <div className="calc-field">
                <label htmlFor="calc-speed">Average speed</label>
                <div className="calc-inline-row">
                  <NumberField
                    id="calc-speed"
                    min="1"
                    step="1"
                    value={state.speedValue}
                    onCommit={(v) => patch({ speedValue: v }, 'speedValue')}
                    data-testid="input-speed-value"
                  />
                  <span className="calc-suffix">{speedUnit}</span>
                </div>
                {result.ok && (
                  <p className="calc-hint" data-testid="drive-time-hint">
                    About {formatDuration(result.driveSeconds)} of driving.
                  </p>
                )}
                {errorMap.speedValue && (
                  <p className="calc-error" role="alert">{errorMap.speedValue}</p>
                )}
              </div>
            </>
          )}

          {solveFor !== 'interval' && (
            <div className="calc-field" key="interval-field">
              <label htmlFor="calc-interval">Capture interval</label>
              <div className="calc-chiprow" role="group" aria-label="Common intervals">
                {INTERVAL_CHIP_SECONDS.map((sec) => (
                  <button
                    key={sec}
                    type="button"
                    className={`calc-chip${state.intervalSeconds === sec ? ' is-active' : ''}`}
                    aria-pressed={state.intervalSeconds === sec}
                    data-testid={`interval-chip-${sec}`}
                    onClick={() => patch({ intervalSeconds: sec }, 'intervalSeconds')}
                  >
                    {formatInterval(sec).replace('every ', '')}
                  </button>
                ))}
              </div>
              <IntervalInput
                id="calc-interval"
                seconds={state.intervalSeconds}
                onChange={(sec) => patch({ intervalSeconds: sec }, 'intervalSeconds')}
              />
              {errorMap.intervalSeconds && (
                <p className="calc-error" role="alert">{errorMap.intervalSeconds}</p>
              )}
            </div>
          )}

          {solveFor !== 'length' && (
            <div className="calc-field" key="target-field">
              <label htmlFor="calc-target">Target video length</label>
              <IntervalInput
                id="calc-target"
                seconds={state.targetVideoSeconds}
                onChange={(sec) => patch({ targetVideoSeconds: sec }, 'targetVideoSeconds')}
                compactUnits
                testIdPrefix="target"
              />
              {errorMap.targetVideoSeconds && (
                <p className="calc-error" role="alert">{errorMap.targetVideoSeconds}</p>
              )}
            </div>
          )}

          <div className="calc-field">
            <span className="calc-label" id="calc-fps-label">Playback frame rate</span>
            <Segmented
              options={FPS_OPTIONS.map((f) => ({ id: f, label: String(f) }))}
              value={state.fps}
              onChange={(f) => patch({ fps: Number(f) }, 'fps')}
              ariaLabel="Playback frame rate"
              testIdPrefix="fps"
            />
            {errorMap.fps && (
              <p className="calc-error" role="alert">{errorMap.fps}</p>
            )}
          </div>

          <details className="calc-disclosure" data-testid="more-options">
            <summary>More options</summary>
            <div className="calc-field">
              <label htmlFor="calc-storage">Storage per captured frame</label>
              <select
                id="calc-storage"
                value={activeStorageId}
                onChange={(e) => {
                  const opt = STORAGE_PRESETS.find((s) => s.id === e.target.value);
                  if (opt) handleStorageSelect(opt);
                }}
                data-testid="input-storage-preset"
              >
                {STORAGE_PRESETS.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
              {(customStorage || activeStorageId === 'custom') && (
                <div className="calc-inline-row">
                  <NumberField
                    min="0.1"
                    step="0.1"
                    value={state.mbPerFrame}
                    onCommit={(mb) => {
                      lastSelfMbRef.current = mb;
                      patch({ mbPerFrame: mb }, 'mbPerFrame');
                    }}
                    aria-label="Custom MB per frame"
                    data-testid="input-custom-mb"
                  />
                  <span className="calc-suffix">MB / frame</span>
                </div>
              )}
              {errorMap.mbPerFrame && (
                <p className="calc-error" role="alert">{errorMap.mbPerFrame}</p>
              )}
            </div>
          </details>

          {/* Last field, and the only one that is not about the camera. "None"
              is one tap away so the finish comparison is easy to opt out of. */}
          <div className="calc-field calc-still-field" data-testid="still-field">
            <label htmlFor="calc-dead">
              <span className="calc-still-title">
                Still footage
                <span className="calc-optional">optional</span>
              </span>
              <span className="calc-slider-value" data-testid="dead-time-value">
                {stillPercent}%
              </span>
            </label>
            <div
              className="calc-chiprow calc-stillchips"
              role="group"
              aria-label="How much still footage"
            >
              {STILL_CHIPS.map((chip) => {
                const active = state.deadTimeFraction === chip.value;
                return (
                  <button
                    key={chip.id}
                    type="button"
                    className={`calc-chip${active ? ' is-active' : ''}`}
                    aria-pressed={active}
                    data-testid={`still-chip-${chip.id}`}
                    onClick={() => patch({ deadTimeFraction: chip.value }, 'deadTimeFraction')}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
            <input
              id="calc-dead"
              type="range"
              min="0"
              max="0.8"
              step="0.05"
              value={state.deadTimeFraction}
              onChange={(e) => patch({ deadTimeFraction: parseFloat(e.target.value) }, 'deadTimeFraction')}
              aria-valuetext={`${stillPercent} percent still footage`}
              style={{ '--calc-fill': `${(state.deadTimeFraction / 0.8) * 100}%` }}
              data-testid="input-dead-time"
            />
            <p className="calc-hint">{STILL_HINTS[scenario] || STILL_HINTS.project}</p>
            {errorMap.deadTimeFraction && (
              <p className="calc-error" role="alert">{errorMap.deadTimeFraction}</p>
            )}
          </div>
        </div>
      </section>

      {showPill && (
        <button
          type="button"
          className="calc-pill"
          data-testid="answer-pill"
          onClick={() => {
            if (answerRef.current && answerRef.current.scrollIntoView) {
              answerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
        >
          <span className="calc-pill-label">{answer.kicker}</span>
          <span className="calc-pill-value">{answer.value}</span>
        </button>
      )}
    </div>
  );
}
